/** How long a page-view waits for the page's title before it goes out without one. */
export const TITLE_WAIT_MS = 1000;

/**
 * Calls `callback` as soon as `document.title` is not empty, or after
 * `timeoutMs` if it never is, whichever comes first. Returns a function that
 * cancels the wait.
 *
 * Next streams a dynamic route's metadata (`/search`), so the title can be
 * blank for a moment after the route has rendered. A page-view sent in that
 * moment would be stored without a title.
 */
export function onceTitleIsSet(callback: () => void, timeoutMs: number = TITLE_WAIT_MS): () => void {
    if (document.title.trim() !== "") {
        callback();
        return () => {};
    }

    let finished = false;

    const observer = new MutationObserver(() => {
        if (document.title.trim() !== "") finish();
    });
    const timer = setTimeout(() => finish(), timeoutMs);

    function stop(): void {
        finished = true;
        observer.disconnect();
        clearTimeout(timer);
    }

    function finish(): void {
        if (finished) return;
        stop();
        callback();
    }

    // The whole document, not just <head>: streamed metadata may put the <title> elsewhere.
    observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });

    return stop;
}
